import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import { subscribeTopic, unsubscribeTopic } from '@/utils/notificationsSocket';
import type { ApprovalSummary } from '../types';
import { APPROVALS_KEY, APPROVAL_SUMMARY_KEY, MY_APPROVALS_KEY } from './useApprovals';

/**
 * Subscribes to the realtime `approvals:{companyId}` gateway topic. On the
 * initial snapshot (and any subsequent counts payload) it forwards the summary
 * counts to `onSnapshot`; on every event it invalidates the react-query
 * `['approvals']` caches so open lists/detail refetch.
 *
 * Snapshot shape: {draft_count, pending_count, approved_count, rejected_count}.
 * Event `data` (camelCase): {requestId, objectType, objectId, status, eventType}.
 */
export const useApprovalsSocket = (
  companyId?: string,
  onSnapshot?: (counts: ApprovalSummary) => void
): void => {
  const queryClient = useQueryClient();
  const token = useSelector((state: any) => state.auth.access_token);

  useEffect(() => {
    if (!companyId) return;
    const topic = `approvals:${companyId}`;

    let active = true;
    const handlePayload = (payload: Record<string, unknown>) => {
      if (!active) return;
      // Snapshot carries the *_count keys; events carry requestId/eventType.
      if (payload && typeof (payload as any).pending_count === 'number') {
        onSnapshot?.(payload as unknown as ApprovalSummary);
        return;
      }
      queryClient.invalidateQueries([APPROVALS_KEY]);
      queryClient.invalidateQueries([MY_APPROVALS_KEY]);
      queryClient.invalidateQueries([APPROVAL_SUMMARY_KEY]);
      // Events carry no counts. Request a new authoritative snapshot, including
      // for employees who cannot call the admin-only REST summary endpoint.
      if (onSnapshot) subscribeTopic(topic, handlePayload);
    };
    subscribeTopic(topic, handlePayload);

    return () => {
      active = false;
      unsubscribeTopic(topic);
    };
    // onSnapshot is expected to be stable (useCallback in the caller).
  }, [companyId, onSnapshot, queryClient, token]);
};
